import React, { useEffect, useState, useRef } from "react";
import PropertyTable from "./PropertyTable";
import SearchLocation from "./SearchLocation";
import { app_url, dateString, disclaimer } from "./shared/Utils";
import { loadScript, handleScriptLoad } from "./shared/googleLoadScript";
import DwellMarketText from "./shared/DwellMarketText";
import DataExport from "./shared/DataExport";
import DefaultSearchRate from "./shared/DefaultSearchRate";
import DefaultSearchLogo from "./shared/DefaultSearchLogo";
import UnitTypeCheckboxes from "./shared/UnitTypeCheckboxes";
import GreenBox from "./shared/GreenBox";
import { useMatomo } from "@datapunt/matomo-tracker-react";

const Dashboard = ({ handlelogout, showLoader }) => {
  const date = new Date();
  const islogged = localStorage.getItem("islogged");
  const { trackPageView, trackEvent } = useMatomo();

  const [rate, setRate] = useState("daily");
  const [frequency] = useState("month");
  const [dashBoardData, setDashBoardData] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [searchedLoc, setSearchedLoc] = useState(null);
  const [marketName, setmarketName] = useState(null);
  const [unitAvailable, setUnitAvailable] = useState(null);
  const [currData, setCurrData] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [currSymbol, setCurrSymbol] = useState(null);
  const [isDefaultSearch, setIsDefaultSearch] = useState(true);
  const [unitType, setUnitType] = useState("1F");
  const [unitTypes, setUnitTypes] = useState(["1F"]);
  const [avgData, setAvgData] = useState(null);

  const autoCompleteRef = useRef(null);

  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  const onChangeRate = (event) => {
    trackEvent({ category: "Rate Type Changed", action: "change-event" });
    setRate(event.target.value);
  };

  const onChangeUnit = (event) => {
    trackEvent({ category: "Unit Type Changed", action: "change-event" });
    setUnitType(event.target.value);
    setUnitTypes([event.target.value]);
  };

  const onChangeUnitCheck = (event) => {
    trackEvent({ category: "Unit Checkbox Clicked", action: "click-event" });
    let currCheck = event.target.value;
    let isChecked = unitTypes.filter((u) => u === currCheck);
    if (isChecked.length !== 0) {
      let finalUnits = unitTypes.filter((u) => u !== currCheck);
      setUnitTypes(finalUnits);
    } else {
      setUnitTypes([...unitTypes, currCheck]);
    }
  };

  const getAvgPrice = (price) => {
    return price
      ? Math.round(price * currData?.conversionRate?.[currency.toLowerCase()])
      : "N/A";
  };

  const handleSearchOutput = () => {
    if (query === "") {
      return false;
    }
    trackEvent({
      category: "Search Rate Button Clicked",
      action: "click-event",
    });
    let geoloc = selectedAddress?.geometry?.location;
    let addr = selectedAddress?.formatted_address;
    setSearchedLoc({
      lat: geoloc?.lat(),
      lng: geoloc?.lng(),
      addr,
      rate,
      frequency,
      query,
    });
    setDashBoardData(null);
    setIsDefaultSearch(false);
  };

  const handleCurrencyChange = (e) => {
    setCurrency(e.target.value);
    trackEvent({ category: "Currency Value Changed", action: "change-event" });
    let currentCurrencyObj = currData?.currencies?.filter(
      (item) => item.currencyName === e.target.value
    );
    setCurrSymbol(currentCurrencyObj?.[0]?.currencySymbol);
  };

  const exportClick = () => {
    trackEvent({ category: "Export Button Clicked", action: "click-event" });
  };

  useEffect(() => {
    trackPageView({
      documentTitle: "Dashboard Page",
      href: window.location.href,
    });
    loadScript(
      `https://maps.googleapis.com/maps/api/js?key=AIzaSyBw9iNXEdAAsUwytxKkExTvBKC0jfSA8DY&libraries=places&${
        dashBoardData === null ? "callback=Function.prototype" : ""
      }`,
      () => handleScriptLoad(setQuery, setSelectedAddress, autoCompleteRef)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (islogged !== null) {
      fetch(`${app_url}marketrates/getcurrencymasterData`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${islogged}`,
        },
      })
        .then((response) => {
          if (response.status === 200) {
            return response.json();
          }

          return response;
        })
        .then((curr) => {
          setCurrData(curr);
          setCurrency("USD");
          setCurrSymbol("$");
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
  }, [islogged]);

  useEffect(() => {
    if (searchedLoc && searchedLoc.lat !== undefined) {
      showLoader(true);
      setmarketName("");
      fetch(
        `${app_url}marketrates/GetMarketSearch?latitude=${searchedLoc.lat}&longitude=${searchedLoc.lng}&rate=${rate}&frequency=${searchedLoc.frequency}&cityName=${searchedLoc.addr}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${islogged}`,
          },
        }
      )
        .then((response) => {
          setStatusCode(response.status);
          if (response.status === 401) {
            handlelogout();
          }
          if (response.status === 200) {
            return response.json();
          }
          return response;
        })
        .then((tabledata) => {
          if (dashBoardData === null) {
            setUnitType("1F");
            setUnitTypes(["1F"]);
          }
          setUnitAvailable(tabledata?.unitSummary);
          setDashBoardData(tabledata?.marketData || []);
          setmarketName(tabledata?.marketName);
          setQuery("");
          showLoader(false);
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedLoc, rate]);

  useEffect(() => {
    if (searchedLoc && searchedLoc.lat !== undefined) {
      setAvgData(null);
      fetch(
        `${app_url}marketrates/GetMarketSummary?latitude=${searchedLoc.lat}&longitude=${searchedLoc.lng}&rate=${rate}&frequency=${searchedLoc.frequency}&cityName=${searchedLoc.addr}&unitType=${unitType}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${islogged}`,
          },
        }
      )
        .then((response) => {
          if (response.status === 401) {
            handlelogout();
          }
          if (response.status === 200) {
            return response.json();
          }

          return response;
        })
        .then((data) => {
          setAvgData(data);
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedLoc, unitType, rate]);

  return (
    <div className="bg-with-search pr">
      <div className="dwell-bg-img pa"></div>
      <div className="header-heading">
        <div className="container header-search">
          <DwellMarketText />
          <div
            className={`header-search-bar pr ${
              isDefaultSearch ? "default-search" : ""
            }`}
          >
            <div
              className={`common-search-bar pr ${
                isDefaultSearch ? "default-search-inner" : ""
              }`}
            >
              {isDefaultSearch ? <DefaultSearchLogo /> : ""}
              <div
                className={`default-search-bar ${
                  !isDefaultSearch ? "m-0" : ""
                }`}
              >
                <SearchLocation
                  handleSearchOutput={handleSearchOutput}
                  handleInputChange={handleInputChange}
                  query={query}
                  selectedAddress={selectedAddress}
                  ref={autoCompleteRef}
                />
              </div>
              {isDefaultSearch ? (
                <DefaultSearchRate rate={rate} onChangeRate={onChangeRate} />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>

      {searchedLoc && searchedLoc.lat !== undefined ? (
        <div className="white-overlay">
          <div className="table-data">
            <GreenBox
              dashBoardData={dashBoardData}
              marketName={marketName}
              query={searchedLoc?.query}
              currSymbol={currSymbol}
              avgData={avgData}
              unitType={unitType}
              rate={rate}
              currency={currency}
              onChangeUnit={onChangeUnit}
              onChangeRate={onChangeRate}
              handleCurrencyChange={handleCurrencyChange}
              currData={currData}
              getAvgPrice={getAvgPrice}
            />

            {dashBoardData?.data?.length !== 0 ? (
              <>
                <div className="export-box">
                  <div className="export-text">Month-by-Month Averages</div>
                  <div className="export-data">
                    <DataExport
                      currSymbol={currSymbol}
                      currRate={
                        currData?.conversionRate?.[currency.toLowerCase()]
                      }
                      marketName={marketName}
                      monthlyAvgPrice={`${currSymbol}${getAvgPrice(
                        avgData?.previousYearAvgPrice
                      )}`}
                      unitTypes={unitTypes}
                      unitType={unitType}
                      rate={rate}
                      currency={currency}
                      lastMonAvg={`${currSymbol}${getAvgPrice(
                        avgData?.previousMonthAvgPrice
                      )}`}
                      lowPriceAvg={`${currSymbol}${getAvgPrice(
                        avgData?.previousYearMinAvgPrice
                      )}`}
                      highPriceAvg={`${currSymbol}${getAvgPrice(
                        avgData?.previousYearMaxAvgPrice
                      )}`}
                      tableData={dashBoardData}
                      unitAvailable={unitAvailable}
                      exportClick={exportClick}
                    />
                  </div>
                </div>
                <UnitTypeCheckboxes
                  onChangeUnitCheck={onChangeUnitCheck}
                  unitTypes={unitTypes}
                  unitType={unitType}
                />
              </>
            ) : (
              ""
            )}
            <div
              className={`table-responsive mat-elevation-z8 ${
                dashBoardData?.data?.length === 0 ? "d-none" : ""
              }`}
            >
              {dashBoardData ? (
                <PropertyTable
                  tableData={dashBoardData}
                  currSymbol={currSymbol}
                  currRate={currData?.conversionRate?.[currency.toLowerCase()]}
                  unitTypes={unitTypes}
                  unitType={unitType}
                  unitAvailable={unitAvailable}
                  statusCode={statusCode}
                />
              ) : (
                ""
              )}
            </div>
            <h6 className="m-0 table-footer">{dateString(date)}</h6>
            <div className="disclaimer t-left">{disclaimer}</div>
          </div>
          <div className="dash-footer">
            <div className="container">
              <div className="footer-cc">
                ©{date.getFullYear()} Dwellworks Living Confidential &
                Proprietary
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default Dashboard;
